/**
 * Region-record validator. Pure-functional discriminated-union result.
 */

import type {
  Region, VerificationStatus,
} from './types.js';

export type ValidationResult =
  | { valid: true; region: Region }
  | { valid: false; errors: string[] };

const VERIFICATION_STATUSES: readonly VerificationStatus[] = ['verified', 'partial', 'unverified'];

function isFiniteNum(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

export function validateRegion(rawJson: string, sourcePath: string): ValidationResult {
  const errors: string[] = [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch (e) {
    return { valid: false, errors: [`${sourcePath}: JSON parse error: ${(e as Error).message}`] };
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return { valid: false, errors: [`${sourcePath}: top-level must be object`] };
  }
  const r = parsed as Record<string, unknown>;

  if (typeof r.id !== 'string' || r.id.length === 0) errors.push('id required');
  if (typeof r.names !== 'object' || r.names === null) errors.push('names required');
  else {
    const n = r.names as Record<string, unknown>;
    if (typeof n.english !== 'string') errors.push('names.english required');
  }
  if (typeof r.hierarchy !== 'object' || r.hierarchy === null) errors.push('hierarchy required');
  else {
    const h = r.hierarchy as Record<string, unknown>;
    if (typeof h.level !== 'number' || ![0, 1, 2, 3].includes(h.level)) {
      errors.push('hierarchy.level must be 0,1,2,3');
    }
  }
  if (typeof r.population !== 'object' || r.population === null) errors.push('population required');
  else {
    const p = r.population as Record<string, unknown>;
    if (!isFiniteNum(p.total)) errors.push('population.total must be finite number');
    if (!isFiniteNum(p.year)) errors.push('population.year required');
    if (!Array.isArray(p.ageStructure)) errors.push('population.ageStructure must be array');
    else if (p.ageStructure.length === 0) errors.push('population.ageStructure must be non-empty');
    else {
      // Check age-band counts sum within 1% of total.
      let bandSum = 0;
      for (let i = 0; i < p.ageStructure.length; i++) {
        const b = p.ageStructure[i] as Record<string, unknown>;
        if (typeof b !== 'object' || b === null) {
          errors.push(`population.ageStructure[${i}] must be object`);
          continue;
        }
        if (!isFiniteNum(b.count)) errors.push(`ageStructure[${i}].count required`);
        if (!isFiniteNum(b.fraction)) errors.push(`ageStructure[${i}].fraction required`);
        if (!isFiniteNum(b.ageBandLow)) errors.push(`ageStructure[${i}].ageBandLow required`);
        if (!isFiniteNum(b.ageBandHigh)) errors.push(`ageStructure[${i}].ageBandHigh required`);
        if (isFiniteNum(b.count)) bandSum += b.count as number;
      }
      if (isFiniteNum(p.total)) {
        const rel = Math.abs(bandSum - (p.total as number)) / (p.total as number);
        if (rel > 0.01) {
          errors.push(`population.ageStructure sums to ${Math.round(bandSum)}, expected ${p.total} (within 1%)`);
        }
      }
    }
    if (!Array.isArray(p.citations) || p.citations.length === 0) {
      errors.push('population.citations required (non-empty)');
    }
  }

  if (typeof r.verification !== 'object' || r.verification === null) {
    errors.push('verification required');
  } else {
    const v = r.verification as Record<string, unknown>;
    if (!VERIFICATION_STATUSES.includes(v.status as VerificationStatus)) {
      errors.push(`verification.status must be one of ${VERIFICATION_STATUSES.join(', ')}`);
    }
    if (!Array.isArray(v.reasons)) errors.push('verification.reasons must be array');
  }
  if (typeof r.snapshot !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(r.snapshot)) {
    errors.push('snapshot must be ISO YYYY-MM-DD');
  }

  return errors.length > 0
    ? { valid: false, errors }
    : { valid: true, region: r as unknown as Region };
}
